import { c as _c } from "react/compiler-runtime";
import { isArray } from 'betterplace-utils';
import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import useValueRef from '../useValueRef';
type Size = {
  width: number | undefined;
  height: number | undefined;
};
type UseResizeObserverOptions<T extends HTMLElement | null = HTMLElement> = {
  ref: RefObject<T>;
  onResize?: (size: Size) => void;
  box?: 'border-box' | 'content-box' | 'device-pixel-content-box';
};
const initialSize: Size = {
  width: undefined,
  height: undefined
};
function useResizeObserver(options) {
  const $ = _c(5);
  const {
    ref,
    box: t0
  } = options;
  const box = t0 === undefined ? "content-box" : t0;
  const [size, setSize] = useState(initialSize);
  const onResize = useValueRef(options.onResize);
  let t1;
  let t2;
  if ($[0] !== box || $[1] !== onResize || $[2] !== ref) {
    t1 = () => {
      if (!ref || !ref.current) {
        return;
      }
      if (!window || typeof window === "undefined" || !("ResizeObserver" in window)) {
        return;
      }
      const observer = new ResizeObserver(t3 => {
        const [entry] = t3;
        if (!ref.current) {
          return;
        }
        const boxProp = box === "border-box" ? "borderBoxSize" : box === "device-pixel-content-box" ? "devicePixelContentBoxSize" : "contentBoxSize";
        if (!entry) {
          return;
        }
        const newSize = extractSize(entry, boxProp);
        setSize(prev => {
          const hasChanged = prev.width !== newSize.width || prev.height !== newSize.height;
          if (!hasChanged) {
            return prev;
          }
          onResize.current?.(newSize);
          return newSize;
        });
      });
      observer.observe(ref.current, {
        box
      });
      const size_0 = {
        width: ref.current.clientWidth,
        height: ref.current.clientHeight
      };
      onResize.current?.(size_0);
      return () => {
        observer.disconnect();
      };
    };
    t2 = [box, onResize, ref];
    $[0] = box;
    $[1] = onResize;
    $[2] = ref;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return size;
}
type BoxSizesKey = keyof Pick<ResizeObserverEntry, 'borderBoxSize' | 'contentBoxSize' | 'devicePixelContentBoxSize'>;
function extractSize(entry: ResizeObserverEntry, box: BoxSizesKey): Size {
  function getFallback(entry: ResizeObserverEntry) {
    return {
      width: entry.contentRect.width,
      height: entry.contentRect.height
    };
  }
  function getSizeFromBoxSize(size: ResizeObserverSize | undefined): Size {
    if (!size || typeof size.inlineSize !== 'number' || typeof size.blockSize !== 'number') return getFallback(entry);
    return {
      width: size.inlineSize,
      height: size.blockSize
    };
  }
  const boxSize = entry[box] as ResizeObserverSize[] | ResizeObserverSize | undefined;
  if (isArray(boxSize)) {
    return getSizeFromBoxSize(boxSize[0]);
  }
  return getSizeFromBoxSize(boxSize);
}
export default useResizeObserver;